import React, { type FC, useCallback, useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import {
  type CgvStylesPayload,
  fetchCgvFromApi,
  resolveCurrentOrganizationId,
} from '../../Services/LegalService';
import { StyledCGVContainer, StyledErrorContainer, StyledLoadingContainer } from './style';

export interface CGVContentProps {
  organizationId?: number | string;
  styles?: Partial<CgvStylesPayload>;
  mockHtml?: string;
}

const CGVContent: FC<CGVContentProps> = ({ organizationId, styles, mockHtml }) => {
  const [html, setHtml] = useState<string | null>(mockHtml ?? null);
  const [loading, setLoading] = useState<boolean>(!mockHtml);
  const [error, setError] = useState<string | null>(null);

  const loadCgv = useCallback(async () => {
    if (mockHtml) {
      setHtml(mockHtml);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orgId = organizationId ?? (await resolveCurrentOrganizationId());
      if (!orgId) {
        throw new Error("Aucune organisation n'a été trouvée pour charger les CGV.");
      }

      const content = await fetchCgvFromApi(orgId, styles);
      setHtml(content);
    } catch (err: any) {
      console.error('Erreur lors du chargement des CGV:', err);
      setError(
        err?.message || 'Une erreur est survenue lors du chargement des Conditions Générales.',
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, styles, mockHtml]);

  useEffect(() => {
    loadCgv();
  }, [loadCgv]);

  if (loading) {
    return (
      <StyledLoadingContainer>
        <CircularProgress />
      </StyledLoadingContainer>
    );
  }

  if (error) {
    return (
      <StyledErrorContainer>
        <p>{error}</p>
        <Button variant="outlined" color="primary" onClick={loadCgv}>
          Réessayer
        </Button>
      </StyledErrorContainer>
    );
  }

  if (!html) {
    return null;
  }

  return <StyledCGVContainer dangerouslySetInnerHTML={{ __html: html }} />;
};

export default CGVContent;
